/* =============================================================================
 *  quotedText - find the quoted history of a reply chain so the reader can
 *  fold it behind a "show quoted text" toggle.
 *
 *  Why: a long client thread is unreadable on a phone - the new message is one
 *  line at the very top and two screens of "On Monday X wrote:" history sit
 *  under it. The reader keeps the WHOLE body in the DOM (copy, find and inline
 *  cid images keep working); quoted parts just get a `data-wc-quoted`
 *  attribute and a stylesheet rule hides them until the toggle is pressed.
 *
 *  Two entry points, both pure (no network, no React):
 *    collapseQuotedHtml(html)  - HTML body, ALREADY sanitised + images resolved.
 *                                Uses DOMParser; only ever ADDS an attribute (or
 *                                a marked <span> around stray text), never
 *                                removes content.
 *    collapseQuotedText(text)  - text/plain body. Escapes the text itself and
 *                                returns the same wrapper markup the reader used
 *                                before, with the quoted lines in marked spans.
 *
 *  Safety rules (see the tests):
 *    - Collapse only when there is real un-quoted content left visible: a
 *      message that is ENTIRELY quoted (or a forward with no note) stays open.
 *    - Collapse only when the marked region really holds content (an empty
 *      Outlook `appendonsend` marker alone must not produce an empty toggle).
 *    - Idempotent: running the output through again gives the same output.
 *    - Nothing detected -> the input string is returned untouched.
 *    - Bounded work: the body (and the subject) are attacker-shaped, so the HTML
 *      detector runs on a hard budget and FAILS OPEN - past it the message is
 *      simply shown whole, never hung (see "Work budget" below).
 * ============================================================================= */

/** Marks an element (or wrapped text run) that belongs to the quoted history. */
export const QUOTED_ATTR = 'data-wc-quoted';
/** Class on the iframe <body> while the quoted history is folded. */
export const QUOTED_COLLAPSED_CLASS = 'wc-quoted-collapsed';
/** Rule for the reader iframe's stylesheet. !important: a sender's own inline
 *  `display:` must not keep quoted text visible. */
export const QUOTED_COLLAPSE_CSS = `body.${QUOTED_COLLAPSED_CLASS} [${QUOTED_ATTR}] { display: none !important; }`;

export interface CollapseResult {
  /** The markup to render (the input, unchanged, when `hasQuoted` is false). */
  html: string;
  /** True when quoted history was found AND collapsing it is safe. */
  hasQuoted: boolean;
}

/** Bodies this large are left alone: parsing them would stall a phone. */
const MAX_BODY_CHARS = 1500000;

// -- Work budget ---------------------------------------------------------------
//
// collapseQuotedHtml runs synchronously on the main thread for every HTML email,
// and the sender picks the markup AND the subject. Several detectors compare a
// node with its neighbours, so a hostile body (thousands of empty <blockquote>s,
// thousands of "wrote:" spans under one wrapper) turns that into O(N^2): a 200 KB
// body froze the tab for ~15 s, every time the message was opened. A genuine mail
// costs a few passes over its own nodes, so the detector gets a hard ceiling and,
// past it, gives up: the message is shown whole, exactly as if it had no quoted
// history. A missed fold is cheap; a hung reader on a phone is not.

/** Bodies with more elements than this are not analysed at all. Real threads are
 *  a fraction of it; the bytes cap alone (1.5M chars) still lets ~100k in. */
export const QUOTED_MAX_ELEMENTS = 25000;
/** More candidate quote regions than this is not a real thread. */
const MAX_CANDIDATES = 1000;
/** Node visits allowed per element, plus a floor for tiny bodies with big text.
 *  Every pass over the tree, every sibling step and every text scan (per 256
 *  chars) is charged against it. */
const WORK_PER_ELEMENT = 24;
const MIN_WORK = 100000;
/** An "On .. wrote:" lead-in is one short line: a candidate longer than this, or
 *  spread over more nodes, cannot be one (and is never read in full). */
const LEAD_MAX_CHARS = 800;
const LEAD_MAX_NODES = 120;

const BUDGET_EXCEEDED = new Error('quotedText: work budget exceeded');
/** Infinite outside collapseQuotedHtml, so the shared helpers cost nothing on the
 *  text path. The function is synchronous, so module state cannot interleave. */
let workLeft = Number.POSITIVE_INFINITY;

function spend(units = 1): void {
  workLeft -= units;
  if (workLeft < 0) throw BUDGET_EXCEEDED;
}

// -- Text helpers ------------------------------------------------------------

const INVISIBLE_RE = /[\u00a0\u200b-\u200f\u2060\ufeff\u034f]/g;
const norm = (s: string | null | undefined): string =>
  (s || '').replace(INVISIBLE_RE, ' ').replace(/\s+/g, ' ').trim();

/** Any character that is not whitespace / an invisible / pure decoration. */
const REAL_TEXT_RE = /[^\s\u00a0\u200b-\u200f\u2060\ufeff\u034f\-_=*.,;:|>~\u2022\u00b7]/;
const hasRealText = (s: string | null | undefined): boolean => {
  if (!s) return false;
  spend(1 + (s.length >> 8)); // the scan is O(length) when the text is all decoration
  return REAL_TEXT_RE.test(s);
};

/** Text of a subtree with a space between every text node. `textContent` glues
 *  "<b>Sent:</b> Mon PM<br><b>To:</b> X" into "PMTo:", which defeats the
 *  header-block test. */
function spacedText(node: Node, maxChars = 1200): string {
  const doc = node.ownerDocument;
  if (isTextNode(node)) return node.data.slice(0, maxChars);
  if (!doc || !isEl(node)) return '';
  // Elements are shown to the filter too (and skipped) only so that every node
  // the walker steps over is charged to the work budget.
  const walker = doc.createTreeWalker(node, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT, {
    acceptNode(n: Node): number {
      spend(1);
      if (isEl(n)) return SKIP_TAGS.has(tagOf(n)) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_SKIP;
      const parent = n.parentNode;
      return isEl(parent) && SKIP_TAGS.has(tagOf(parent)) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT;
    },
  });
  let out = '';
  for (let n = walker.nextNode(); n && out.length < maxChars; n = walker.nextNode()) {
    // Clipped: one giant text node must not make every caller pay for all of it.
    out += ` ${(n as Text).data.slice(0, maxChars)}`;
  }
  return out;
}

/** `node`'s text exactly as textContent would give it (no separators), or null
 *  when it runs past `maxChars` / `maxNodes`: too big to be a one-line lead-in.
 *  textContent on a shared wrapper is the whole body, so asking it once per
 *  "wrote:" candidate is O(body) each. */
function boundedText(node: Node, maxChars: number, maxNodes: number): string | null {
  if (isTextNode(node)) return node.data.length > maxChars ? null : node.data;
  let out = '';
  let visited = 0;
  let cur: Node | null = node.firstChild;
  while (cur) {
    spend(1);
    if (++visited > maxNodes) return null;
    if (isTextNode(cur)) {
      out += cur.data;
      if (out.length > maxChars) return null;
    }
    // Next node in document order, staying inside `node`.
    let next: Node | null = cur.firstChild;
    while (!next && cur && cur !== node) {
      next = cur.nextSibling;
      if (!next) cur = cur.parentNode;
    }
    cur = next;
  }
  return out;
}

const escapeText = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

// "On Mon, 29 Sep 2026 at 3:12 PM John <john@x.com> wrote:" and a few locales.
// Needs a digit, '@' or '<' so a sentence like "On Monday he wrote:" is not
// mistaken for an attribution line.
const ATTR_START_RE = /^(?:on|am|le|el|il|em|den|op)\s/i;
// English/Romance put the verb last ("... wrote:"); German/Dutch/Nordic put the
// name after it ("... schrieb Ben <ben@x.com>:").
const ATTR_END_RE = /(?:(?:wrote|a \u00e9crit|a ecrit|escribi\u00f3|escribio|ha scritto|escreveu)\s*:|(?:schrieb|schreef|skrev)[^:]{0,200}:)\s*$/i;

export function isAttributionLine(s: string): boolean {
  const t = norm(s);
  if (t.length < 12 || t.length > 320) return false;
  if (!ATTR_START_RE.test(t) || !ATTR_END_RE.test(t)) return false;
  return /[\d@<]/.test(t);
}

/** "From: .. Sent: .. To: .. Subject: .." - the Outlook reply/forward header. */
export function looksLikeHeaderBlock(s: string): boolean {
  const t = norm(s).replace(/^[-_\s]+/, '').slice(0, 800);
  if (!/^from\s*:/i.test(t)) return false;
  let hits = 0;
  if (/\b(?:sent|date)\s*:/i.test(t)) hits++;
  if (/\bto\s*:/i.test(t)) hits++;
  if (/\bsubject\s*:/i.test(t)) hits++;
  if (/\bcc\s*:/i.test(t)) hits++;
  return hits >= 2;
}

// Leading dashes (or "Begin") are required for forwards: a bare sentence that
// merely starts with "Forwarded message" is not a divider.
const FORWARD_MARKER_RE = /^(?:-{2,}\s*forwarded\s+message\b|begin\s+forwarded\s+message\b)/i;
const ORIGINAL_MARKER_RE = /^-{2,}\s*original\s+message\s*-{2,}/i;
const UNDERSCORE_RE = /^_{10,}/;

// -- DOM helpers ---------------------------------------------------------------

const isEl = (n: Node | null | undefined): n is Element => !!n && n.nodeType === 1;
const isTextNode = (n: Node | null | undefined): n is Text => !!n && n.nodeType === 3;
const tagOf = (el: Element): string => el.tagName.toUpperCase();
const hasClass = (el: Element, cls: string): boolean => el.classList.contains(cls);

const SKIP_TAGS = new Set(['STYLE', 'SCRIPT', 'TITLE', 'TEMPLATE', 'NOSCRIPT', 'HEAD', 'META', 'LINK']);
const MEDIA_TAGS = new Set(['IMG', 'VIDEO', 'AUDIO', 'SVG', 'CANVAS', 'PICTURE', 'IFRAME', 'OBJECT', 'EMBED']);
// Never climb OUT of these when extending a "from here to the end" mark: a
// marker inside a table cell must not swallow the rest of the page layout.
const BOUNDARY_TAGS = new Set([
  'TABLE', 'THEAD', 'TBODY', 'TFOOT', 'TR', 'TD', 'TH', 'CAPTION', 'COLGROUP',
  'UL', 'OL', 'LI', 'DL', 'DT', 'DD', 'BLOCKQUOTE', 'PRE',
]);
const BLOCK_TAGS = new Set([
  'DIV', 'P', 'TABLE', 'UL', 'OL', 'BLOCKQUOTE', 'PRE', 'HR', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
  'SECTION', 'ARTICLE', 'FIELDSET', 'CENTER', 'DL', 'TR', 'TD',
]);

/** A tracking pixel / spacer is not content. */
function isTinyImage(el: Element): boolean {
  if (tagOf(el) !== 'IMG') return false;
  const dim = (attr: string) => {
    const v = parseInt(el.getAttribute(attr) || '', 10);
    return Number.isFinite(v) ? v : null;
  };
  const w = dim('width');
  const h = dim('height');
  if ((w !== null && w <= 2) || (h !== null && h <= 2)) return true;
  const style = (el.getAttribute('style') || '').toLowerCase();
  return /(?:^|;)\s*(?:width|height)\s*:\s*[0-2]px/.test(style);
}

/** Real text, or real media (not a tracking pixel), as a node of its own. */
function isRealContentNode(n: Node): boolean {
  if (isTextNode(n)) return hasRealText(n.data);
  return isEl(n) && MEDIA_TAGS.has(tagOf(n)) && !isTinyImage(n);
}

/** Walks the elements and text of `root`, leaving out <style> & co and (with
 *  `skipMarked`) regions already marked as quoted. Every node visited is
 *  charged to the work budget. */
function contentWalker(root: Node, skipMarked: boolean): TreeWalker | null {
  const doc = root.ownerDocument;
  if (!doc) return null;
  return doc.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT, {
    acceptNode(n: Node): number {
      spend(1);
      if (isEl(n)) {
        if (SKIP_TAGS.has(tagOf(n))) return NodeFilter.FILTER_REJECT;
        if (skipMarked && n.hasAttribute(QUOTED_ATTR)) return NodeFilter.FILTER_REJECT;
      }
      return NodeFilter.FILTER_ACCEPT;
    },
  });
}

/** True when `root`'s subtree shows real text or real media. With
 *  `skipMarked`, regions already marked as quoted do not count. */
function hasRealContent(root: Node, skipMarked: boolean): boolean {
  spend(1);
  if (isTextNode(root)) return hasRealText(root.data);
  if (!isEl(root)) return false;
  if (SKIP_TAGS.has(tagOf(root))) return false;
  if (skipMarked && root.hasAttribute(QUOTED_ATTR)) return false;
  if (isRealContentNode(root)) return true;
  const walker = contentWalker(root, skipMarked);
  if (!walker) return false;
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    if (isRealContentNode(n)) return true;
  }
  return false;
}

/** Document order of the body's nodes plus its last piece of real content,
 *  each computed at most once per message. hasContentAfter used to rescan
 *  everything following every <blockquote> (O(N^2)); with these it is a compare. */
interface DocIndex {
  /** Position of `n` in document order (elements and text). */
  order(n: Node): number;
  /** The last real text / media node of the body, or null. */
  lastContent(): Node | null;
}

function createDocIndex(root: Element): DocIndex {
  let order: Map<Node, number> | null = null;
  let last: Node | null | undefined;
  return {
    order(n: Node): number {
      if (!order) {
        order = new Map<Node, number>();
        const doc = root.ownerDocument;
        if (doc) {
          const walker = doc.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
          let i = 0;
          for (let cur = walker.nextNode(); cur; cur = walker.nextNode()) {
            spend(1);
            order.set(cur, i++);
          }
        }
      }
      return order.get(n) || 0;
    },
    lastContent(): Node | null {
      if (last === undefined) {
        last = null;
        const walker = contentWalker(root, false);
        if (walker) {
          for (let n = walker.nextNode(); n; n = walker.nextNode()) {
            if (isRealContentNode(n)) last = n;
          }
        }
      }
      return last;
    },
  };
}

/** Real content anywhere after `node` in document order. Nothing is marked yet
 *  while candidates are collected, so the `skipMarked` of hasRealContent would
 *  not change the answer here. */
function hasContentAfter(node: Node, index: DocIndex): boolean {
  const last = index.lastContent();
  // Content INSIDE `node` is not "after" it.
  return !!last && index.order(last) > index.order(node) && !node.contains(last);
}

function hasContentBefore(node: Node): boolean {
  for (let s = node.previousSibling; s; s = s.previousSibling) {
    spend(1);
    if (hasRealContent(s, false)) return true;
  }
  return false;
}

/** Mark one node as quoted. Elements get the attribute; a stray run of text
 *  (which cannot carry one) is wrapped in a marked <span>. */
function markNode(n: Node): void {
  if (isEl(n)) {
    n.setAttribute(QUOTED_ATTR, '1');
    return;
  }
  if (isTextNode(n) && hasRealText(n.data) && n.parentNode && n.ownerDocument) {
    const span = n.ownerDocument.createElement('span');
    span.setAttribute(QUOTED_ATTR, '1');
    n.parentNode.replaceChild(span, n);
    span.appendChild(n);
  }
}

/** Mark `start` and everything after it in document order, climbing through
 *  flow containers (but never out of table / list structure). */
function markTail(start: Node, root: Element): void {
  let cur: Node = start;
  let first = true;
  while (cur && cur !== root) {
    // Snapshot: markNode may swap text nodes for spans while we walk.
    const siblings: Node[] = [];
    for (let s: Node | null = first ? cur : cur.nextSibling; s; s = s.nextSibling) {
      spend(1);
      siblings.push(s);
    }
    siblings.forEach(markNode);
    first = false;
    const parent: Node | null = cur.parentNode;
    if (!parent || parent === root || !isEl(parent) || BOUNDARY_TAGS.has(tagOf(parent))) break;
    cur = parent;
  }
}

/** The outermost wrapper for which `node` is the first meaningful thing. */
function climbWhileFirst(node: Node, root: Element): Node {
  let cur: Node = node;
  for (;;) {
    const parent: Node | null = cur.parentNode;
    if (!parent || parent === root || !isEl(parent) || BOUNDARY_TAGS.has(tagOf(parent))) break;
    if (hasContentBefore(cur)) break;
    cur = parent;
  }
  return cur;
}

/** Previous sibling that is not whitespace-only text or a comment. */
function prevSignificant(node: Node): Node | null {
  let p = node.previousSibling;
  while (p && (p.nodeType === 8 || (isTextNode(p) && !norm(p.data)))) {
    spend(1);
    p = p.previousSibling;
  }
  return p;
}

/** Next sibling that is not whitespace-only text or a comment (and, with
 *  `skipBr`, not a line break either). */
function nextSignificant(node: Node, skipBr = false): Node | null {
  let n = node.nextSibling;
  while (n && (n.nodeType === 8
    || (isTextNode(n) && !norm(n.data))
    || (skipBr && isEl(n) && tagOf(n) === 'BR'))) {
    spend(1);
    n = n.nextSibling;
  }
  return n;
}

/**
 * The "On <date> <who> wrote:" lead-in directly before a quote, as the list
 * of nodes to fold with it. Handles a block of its own (<div>, <p>,
 * .moz-cite-prefix, .gmail_attr) and the inline form where the sentence is
 * split across text / <a> / <br> siblings. Climbs out of up to two plain
 * wrappers (Apple Mail nests the quote in a <div> after the lead-in div).
 */
function attributionBefore(target: Node, root: Element): Node[] {
  let t: Node = target;
  for (let level = 0; level < 3; level++) {
    const found = attributionSiblings(t);
    if (found.length) return found;
    const parent: Node | null = t.parentNode;
    if (!parent || parent === root || !isEl(parent) || BOUNDARY_TAGS.has(tagOf(parent))) break;
    if (hasContentBefore(t)) break;
    t = parent;
  }
  return [];
}

function attributionSiblings(target: Node): Node[] {
  const collected: Node[] = [];
  let acc = '';
  let sib: Node | null = target.previousSibling;
  for (let steps = 0; sib && steps < 10; steps++) {
    spend(1);
    if (isTextNode(sib)) {
      // A lead-in is one short line; never normalise a giant text node for it.
      if (sib.data.length > LEAD_MAX_CHARS) return [];
      const t = norm(sib.data);
      collected.push(sib);
      if (t) acc = norm(`${t} ${acc}`);
    } else if (isEl(sib)) {
      const tag = tagOf(sib);
      if (tag === 'BR') {
        collected.push(sib);
        sib = sib.previousSibling;
        continue;
      }
      if (hasClass(sib, 'moz-cite-prefix') || hasClass(sib, 'gmail_attr')) return [...collected, sib];
      collected.push(sib);
      const sibText = boundedText(sib, LEAD_MAX_CHARS, LEAD_MAX_NODES);
      if (sibText === null) return []; // far too long to be part of a lead-in
      acc = norm(`${sibText} ${acc}`);
      // A block-level lead-in stands alone; do not glue it to earlier text.
      if (BLOCK_TAGS.has(tag)) return isAttributionLine(acc) ? collected : [];
    }
    if (isAttributionLine(acc)) return collected;
    if (acc.length > 320) return [];
    sib = sib.previousSibling;
  }
  return [];
}

// -- Candidate collection ------------------------------------------------------

interface Candidate {
  node: Node;
  /** self: fold just this node; tail: fold it and everything after it. */
  kind: 'self' | 'tail';
  /** Extra nodes folded with it (the attribution lead-in). */
  leads?: Node[];
}

const SELF_SELECTOR = [
  '.gmail_quote', '.yahoo_quoted', '.protonmail_quote', '.moz-forward-container',
  '#mail-editor-reference-message-container', 'blockquote[type="cite"]', 'blockquote[cite]',
].join(',');

const OUTLOOK_ID_RE = /^(?:x_)*(?:divRplyFwdMsg|appendonsend)$/i;

function isEmptyDiv(el: Element): boolean {
  return tagOf(el) === 'DIV' && !hasRealContent(el, false);
}

/** Gmail replies can interleave answers between several quote blocks inside one
 *  div.gmail_quote; fold only the lead-in and each blockquote then, so the
 *  answers stay visible. A forward (no blockquote) folds as a whole. */
function gmailTargets(el: Element): Element[] {
  const kids = Array.from(el.children);
  if (tagOf(el) !== 'DIV' || !kids.some((k) => tagOf(k) === 'BLOCKQUOTE')) return [el];
  return kids.filter((k) => tagOf(k) === 'BLOCKQUOTE' || hasClass(k, 'gmail_attr'));
}

export interface CollapseOptions {
  /** The message subject. A bare <blockquote> with no client markup and no
   *  "On .. wrote:" lead-in is only folded when the subject says this is a
   *  reply / forward (Re: / Fwd:) - otherwise it may be a pull-quote. */
  subject?: string | null;
}

/** "Re: x", "RE: x", "Fwd: x", "FW: x", "AW:" / "WG:" (German), "SV:" ... */
export const looksLikeReplySubject = (subject: string | null | undefined): boolean =>
  /^\s*(?:re|fwd?|aw|wg|sv|vs)\s*:/i.test(String(subject || ''));

function collectCandidates(body: Element, opts: CollapseOptions, index: DocIndex): Candidate[] {
  const out: Candidate[] = [];
  const seen = new Set<Node>();
  const add = (c: Candidate) => {
    if (seen.has(c.node)) return;
    // Not a real thread: give up instead of grinding through the rest.
    if (out.length >= MAX_CANDIDATES) throw BUDGET_EXCEEDED;
    seen.add(c.node);
    out.push(c);
  };

  // 1. Client-specific quote containers.
  Array.from(body.querySelectorAll(SELF_SELECTOR)).forEach((el) => {
    const targets = hasClass(el, 'gmail_quote') ? gmailTargets(el) : [el];
    targets.forEach((t) => {
      add({ node: t, kind: 'self', leads: tagOf(t) === 'BLOCKQUOTE' ? attributionBefore(t, body) : undefined });
    });
  });

  // 2. Plain <blockquote>: only when it reads like a reply - an "On .. wrote:"
  //    lead-in, or (Re:/Fwd: subject) nothing meaningful follows it. A bare
  //    blockquote in an ordinary message is usually a pull-quote or a notice
  //    the reader must still see.
  const replySubject = looksLikeReplySubject(opts.subject);
  Array.from(body.querySelectorAll('blockquote')).forEach((bq) => {
    if (seen.has(bq)) return;
    const leads = attributionBefore(bq, body);
    if (leads.length || (replySubject && !hasContentAfter(bq, index))) add({ node: bq, kind: 'self', leads });
  });

  // 3. Outlook web: #appendonsend / <hr> / #divRplyFwdMsg, then the quote.
  Array.from(body.querySelectorAll('[id$="divRplyFwdMsg"], [id$="appendonsend"]')).forEach((el) => {
    if (!OUTLOOK_ID_RE.test(el.id)) return;
    let start: Node = el;
    let p = prevSignificant(start);
    for (let i = 0; i < 3 && p; i++) {
      const isSeparator = isEl(p) && (tagOf(p) === 'HR' || OUTLOOK_ID_RE.test(p.id) || isEmptyDiv(p));
      if (!isSeparator) break;
      start = p;
      p = prevSignificant(start);
    }
    add({ node: start, kind: 'tail' });
  });

  // 4. Outlook desktop / Mac.
  Array.from(body.querySelectorAll('.OutlookMessageHeader')).forEach((el) => add({ node: el, kind: 'tail' }));
  Array.from(body.querySelectorAll('div[style], p[style]')).forEach((el) => {
    const style = (el.getAttribute('style') || '').toLowerCase();
    if (style.indexOf('border-top') === -1) return;
    if (looksLikeHeaderBlock(spacedText(el))) add({ node: el, kind: 'tail' });
  });
  Array.from(body.querySelectorAll('hr')).forEach((hr) => {
    let start: Node = hr;
    let next = nextSignificant(start);
    // <hr> often sits alone in a wrapper div; the header block follows THAT.
    if (!next && isEl(hr.parentNode) && hr.parentNode !== body && tagOf(hr.parentNode) === 'DIV') {
      start = hr.parentNode;
      next = nextSignificant(start);
    }
    if (isEl(next) && looksLikeHeaderBlock(spacedText(next))) add({ node: start, kind: 'tail' });
  });

  // 5. Text-level dividers and lead-ins.
  const doc = body.ownerDocument;
  if (doc) {
    const walker = doc.createTreeWalker(body, NodeFilter.SHOW_TEXT, {
      acceptNode(n: Node): number {
        const parent = n.parentNode;
        return isEl(parent) && SKIP_TAGS.has(tagOf(parent)) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT;
      },
    });
    for (let n = walker.nextNode(); n; n = walker.nextNode()) {
      spend(1);
      const text = n as Text;
      const t = norm(text.data);
      if (t.length < 4) continue;
      if (ORIGINAL_MARKER_RE.test(t) || FORWARD_MARKER_RE.test(t)) {
        add({ node: climbWhileFirst(text, body), kind: 'tail' });
        continue;
      }
      if (UNDERSCORE_RE.test(t)) {
        // Outlook plain divider: a rule of underscores, then the header block.
        const rest = norm(`${t} ${norm(nextTextAfter(text, 600))}`);
        if (looksLikeHeaderBlock(rest)) add({ node: climbWhileFirst(text, body), kind: 'tail' });
        continue;
      }
      // A node over 320 chars can never sit inside a lead-in (nor can any
      // ancestor of it), so skip it before the regex has to read all of it.
      if (t.length <= 320 && ATTR_END_RE.test(t)) {
        const holder = attributionHolder(text, body);
        if (!holder) continue;
        // Inside a quote container (Gmail's .gmail_attr, Thunderbird's cite
        // prefix): the container's own rule already folds it.
        const hostEl = isEl(holder) ? holder : holder.parentElement;
        if (hostEl && (hostEl.closest(SELF_SELECTOR) || hostEl.closest('.moz-cite-prefix, .gmail_attr'))) continue;
        const after = nextSignificant(holder, true);
        // Followed by a quote block: that block's own rule folds both.
        if (isEl(after) && (tagOf(after) === 'BLOCKQUOTE' || after.matches(SELF_SELECTOR))) continue;
        add({ node: climbWhileFirst(holder, body), kind: 'tail' });
      }
    }
  }
  return out;
}

/** The text that follows `node` in document order (for header look-ahead). */
function nextTextAfter(node: Node, maxChars: number): string {
  let out = '';
  let cur: Node | null = node;
  while (cur && out.length < maxChars) {
    for (let s: Node | null = cur.nextSibling; s && out.length < maxChars; s = s.nextSibling) {
      spend(1);
      out += ` ${isTextNode(s) ? s.data.slice(0, maxChars) : isEl(s) && !SKIP_TAGS.has(tagOf(s)) ? spacedText(s, maxChars) : ''}`;
    }
    cur = cur.parentNode;
    if (isEl(cur) && tagOf(cur) === 'BODY') break;
  }
  return out;
}

/** The smallest ancestor of a "...wrote:" text node that is exactly an
 *  attribution line (the sentence can be split across <a> and text nodes). */
function attributionHolder(text: Text, root: Element): Node | null {
  if (isAttributionLine(text.data)) return text;
  let best: Node | null = null;
  let cur: Node | null = text.parentNode;
  for (let i = 0; i < 4 && cur && cur !== root && isEl(cur); i++) {
    if (BOUNDARY_TAGS.has(tagOf(cur))) break;
    // Not textContent: on a shared wrapper that is the WHOLE body, per candidate.
    const raw = boundedText(cur, LEAD_MAX_CHARS, LEAD_MAX_NODES);
    if (raw === null) break;
    const t = norm(raw);
    if (t.length > 320) break;
    if (isAttributionLine(t)) best = cur;
    cur = cur.parentNode;
  }
  return best;
}

// -- HTML entry point ------------------------------------------------------------

const QUOTE_HINT_RE = new RegExp(
  [
    'blockquote', 'gmail_quote', 'yahoo_quoted', 'protonmail_quote', 'moz-forward', 'divrplyfwdmsg',
    'appendonsend', 'outlookmessageheader', 'mail-editor-reference', 'border-top', '<hr',
    'original\\s+message', 'forwarded\\s+message', 'wrote\\s*:', 'schrieb', '\u00e9crit', 'escribi',
    'scritto', 'escreveu', 'skrev', 'schreef',
  ].join('|'),
  'i',
);

export function collapseQuotedHtml(html: string, options: CollapseOptions = {}): CollapseResult {
  const unchanged: CollapseResult = { html, hasQuoted: false };
  if (!html || html.length > MAX_BODY_CHARS) return unchanged;
  // Cheap gate so ordinary mail never pays for a DOM parse.
  if (!QUOTE_HINT_RE.test(html)) return unchanged;
  if (typeof DOMParser === 'undefined') return unchanged;

  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const body = doc.body;
    if (!body) return unchanged;

    // The work budget (see the top of this file) scales with the body, so a big
    // but ordinary thread is analysed and a hostile one is abandoned.
    const elementCount = body.getElementsByTagName('*').length;
    if (elementCount > QUOTED_MAX_ELEMENTS) return unchanged;
    workLeft = MIN_WORK + WORK_PER_ELEMENT * elementCount;

    // Only our own detection may set the attribute: drop any a sender wrote.
    Array.from(body.querySelectorAll(`[${QUOTED_ATTR}]`)).forEach((el) => el.removeAttribute(QUOTED_ATTR));

    const index = createDocIndex(body);
    const candidates = collectCandidates(body, options, index);
    if (!candidates.length) return unchanged;
    // Document order from one indexed pass: compareDocumentPosition scans the
    // siblings, so sorting with it is O(siblings) per comparison.
    const position = new Map<Candidate, number>();
    candidates.forEach((c) => position.set(c, index.order(c.node)));
    candidates.sort((a, b) => (position.get(a) as number) - (position.get(b) as number));

    for (const c of candidates) {
      const host = isEl(c.node) ? c.node : c.node.parentElement;
      // Already inside a folded region (a nested reply level): nothing to add.
      if (host && host.closest(`[${QUOTED_ATTR}]`)) continue;
      if (c.kind === 'self') {
        markNode(c.node);
        (c.leads || []).forEach(markNode);
      } else {
        markTail(c.node, body);
        break; // everything after this point is folded already
      }
    }

    const marked = Array.from(body.querySelectorAll(`[${QUOTED_ATTR}]`));
    if (!marked.length) return unchanged;
    // Nothing real left on show -> an all-quote message / forward without a note.
    if (!hasRealContent(body, true)) return unchanged;
    // The folded region is empty (an Outlook marker with nothing under it).
    if (!marked.some((m) => hasRealContent(m, false))) return unchanged;

    // A fragment's leading <style> blocks are hoisted into <head> by the
    // parser; keep them (they are valid anywhere in the body).
    const headStyles = Array.from(doc.head ? doc.head.querySelectorAll('style') : [])
      .map((s) => s.outerHTML).join('');
    const folded = headStyles + body.innerHTML;
    // The reader sanitises this markup AGAIN, and DOMPurify hoists a LEADING
    // <style> into its own <head> and drops it - so a folded Outlook-web reply
    // (which opens with its paragraph-spacing <style>) lost its styles while the
    // same reply unfolded kept them. An empty <span> in front keeps the styles in
    // the body; once it is there the output no longer starts with <style>, so
    // running this function on its own output still changes nothing.
    return { html: /^\s*<style[\s>]/i.test(folded) ? `<span></span>${folded}` : folded, hasQuoted: true };
  } catch {
    // Includes the work-budget bail-out: fail open, show the message whole.
    return unchanged;
  } finally {
    workLeft = Number.POSITIVE_INFINITY;
  }
}

// -- Plain-text entry point ------------------------------------------------------

const QUOTED_LINE_RE = /^\s{0,3}>/;

/** How many lines (1-3) form an attribution line starting at `i`, else 0. */
function attributionSpanAt(lines: string[], i: number): number {
  if (!ATTR_START_RE.test(lines[i].trim())) return 0;
  let joined = '';
  for (let span = 1; span <= 3 && i + span <= lines.length; span++) {
    joined = `${joined} ${lines[i + span - 1]}`;
    if (isAttributionLine(joined)) return span;
  }
  return 0;
}

/** A divider after which everything is the earlier message. */
function tailMarkerAt(lines: string[], i: number): boolean {
  const t = norm(lines[i]);
  if (!t) return false;
  if (ORIGINAL_MARKER_RE.test(t) || FORWARD_MARKER_RE.test(t)) return true;
  const header = () => lines.slice(i, i + 7).join('\n');
  if (UNDERSCORE_RE.test(t) && /^_+$/.test(t)) {
    let k = i + 1;
    while (k < lines.length && !lines[k].trim()) k++;
    return k < lines.length && looksLikeHeaderBlock(lines.slice(k, k + 7).join('\n'));
  }
  return /^from\s*:/i.test(t) && looksLikeHeaderBlock(header());
}

function classifyTextLines(lines: string[]): boolean[] {
  const n = lines.length;
  const quoted = new Array<boolean>(n).fill(false);
  let i = 0;
  while (i < n) {
    const line = lines[i];
    if (QUOTED_LINE_RE.test(line)) {
      quoted[i] = true;
      i++;
      continue;
    }
    if (tailMarkerAt(lines, i)) {
      for (let k = i; k < n; k++) quoted[k] = true;
      break;
    }
    const span = attributionSpanAt(lines, i);
    if (span) {
      let k = i + span;
      while (k < n && !lines[k].trim()) k++;
      if (k < n && QUOTED_LINE_RE.test(lines[k])) {
        // The lead-in joins the "> " run that follows it.
        for (let m = i; m < i + span; m++) quoted[m] = true;
        i += span;
        continue;
      }
      // Lead-in with an unprefixed quote under it: the rest is the old message.
      for (let m = i; m < n; m++) quoted[m] = true;
      break;
    }
    i++;
  }
  // Blank lines between two quoted lines (or trailing one) belong to the quote.
  for (let k = 0; k < n; k++) {
    if (quoted[k] || lines[k].trim()) continue;
    let end = k;
    while (end + 1 < n && !lines[end + 1].trim() && !quoted[end + 1]) end++;
    const before = k > 0 && quoted[k - 1];
    const after = end + 1 >= n || quoted[end + 1];
    if (before && after) for (let m = k; m <= end; m++) quoted[m] = true;
    k = end;
  }
  return quoted;
}

/**
 * Render a text/plain body the way the reader always has (an escaped
 * `white-space: pre-wrap` block) and fold its quoted history: "> " line runs,
 * "On <date> <who> wrote:" lead-ins, "-----Original Message-----" / Outlook
 * header dividers and forward markers. The text is escaped HERE, so the
 * returned markup is safe by construction.
 */
export function collapseQuotedText(text: string): CollapseResult {
  const src = String(text || '');
  const plain = (): CollapseResult => ({
    html: `<div style="white-space: pre-wrap;">${escapeText(src)}</div>`,
    hasQuoted: false,
  });
  if (!src || src.length > MAX_BODY_CHARS) return plain();

  const lines = src.replace(/\r\n?/g, '\n').split('\n');
  const flags = classifyTextLines(lines);
  if (!flags.some(Boolean)) return plain();

  const segments: Array<{ quoted: boolean; lines: string[] }> = [];
  lines.forEach((line, idx) => {
    const last = segments[segments.length - 1];
    if (last && last.quoted === flags[idx]) last.lines.push(line);
    else segments.push({ quoted: flags[idx], lines: [line] });
  });
  // Same two guards as the HTML path.
  if (!segments.some((s) => !s.quoted && s.lines.some(hasRealText))) return plain();
  if (!segments.some((s) => s.quoted && s.lines.some(hasRealText))) return plain();

  // The newline BETWEEN segments travels with the hidden side so that folding
  // neither glues two visible runs together nor leaves a blank first line.
  let out = '';
  segments.forEach((seg, i) => {
    const body = escapeText(seg.lines.join('\n'));
    const hasNext = i < segments.length - 1;
    if (seg.quoted) {
      out += `<span ${QUOTED_ATTR}="1">${i > 0 ? '\n' : ''}${body}${i === 0 && hasNext ? '\n' : ''}</span>`;
    } else {
      const prevIsLeadingQuote = i === 1 && segments[0].quoted;
      out += `${i > 0 && !prevIsLeadingQuote ? '\n' : ''}${body}`;
    }
  });
  return { html: `<div style="white-space: pre-wrap;">${out}</div>`, hasQuoted: true };
}

/** Fold / unfold an already-rendered reader document (same-origin iframe). */
export function setQuotedCollapsed(doc: Document | null | undefined, collapsed: boolean): void {
  const body = doc && doc.body;
  if (!body) return;
  body.classList.toggle(QUOTED_COLLAPSED_CLASS, collapsed);
}
